import React from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import { Helmet } from 'react-helmet-async';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RootState } from 'modules';
import { ConsultingProgramAboutWrapper } from 'components/programAbout/ConsultingProgramAbout';
import { ERoute } from 'enums/route.enum';
import { Avatar, List, Col, Row, Typography, Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { device, PrimaryColor, SecondaryColor } from 'GlobalStyles';
import IconBagSource from 'assets/icons/bag.svg';
import IconCheckSource from 'assets/icons/check2.svg';
import GoldLogoSource from 'assets/images/logos/goldmansachs.svg';
import HSBCLogoSource from 'assets/images/logos/hsbc.svg';
import JpLogoSource from 'assets/images/logos/jp.svg';
import MorganLogoSource from 'assets/images/logos/morganstanley.svg';
import FinanceProgramAboutSource1 from 'assets/images/pages/programAbout/finance_about_1.webp';
import FinanceProgramAboutSource2 from 'assets/images/pages/programAbout/finance_about_2.webp';
import FinanceProgramAboutSource3 from 'assets/images/pages/programAbout/finance_about_3.webp';

const { Text, Title } = Typography;

const FinanceProgramAbout = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  return (
    <ConsultingProgramAboutWrapper>
      <Helmet>
        <title>Finance - 프로그램 소개</title>
      </Helmet>
      <div className='title-mentoring'>
        <Row align='middle' style={{ maxWidth: '1140px', margin: '0 auto', height: '100%' }}>
          <Col>
            <Title>프로그램 소개</Title>
            <Text style={{ marginTop: '10px' }}>
              Global Finance Firm 입사를 위한
              <br />
              Career Step 만의 멘토링 프로그램
            </Text>
          </Col>
        </Row>
      </div>
      <div className='container' style={{ marginTop: '100px', marginBottom: '100px' }}>
        <div
          style={{
            padding: '0px 8px',
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#000',
            marginBottom: '70px',
          }}
        >
          Career Step Finance 멘토들의 전/현직 직장:
          <div style={{ maxWidth: '900px', margin: '40px auto' }}>
            <Row gutter={[32, 32]} justify='space-between' align='middle'>
              <Col>
                <img alt='GoldLogo' src={GoldLogoSource} className='company-logo' />
              </Col>
              <Col>
                <img alt='MorganLogo' src={MorganLogoSource} className='company-logo-wide' />
              </Col>
              <Col>
                <img alt='JpLogo' src={JpLogoSource} className='company-logo-wide' />
              </Col>
              <Col>
                <img alt='HSBCLogo' src={HSBCLogoSource} className='company-logo-wide' />
              </Col>
            </Row>
          </div>
        </div>
        <Title level={2} style={{ padding: '0px 8px' }}>
          Career Step 프로그램
        </Title>
        <div style={{ padding: '0px 8px', color: '#000', fontSize: '16px' }}>
          Career Step은 Global Finance Firm 조인을 위한 멘토 매칭 플랫폼을 운영 중입니다.
        </div>
        <div style={{ margin: '70px auto' }}>
          <Row align='middle' gutter={[64, 16]} justify='space-between' style={{ marginBottom: '40px' }}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <p className='step-info step-left'>
                <Title level={2}>Frameworks</Title>
                <List split={false}>
                  <List.Item.Meta
                    avatar={<CheckOutlined />}
                    description='Global Finance Firm 인터뷰를 효과적으로 준비할 수 있는 방법을 담은 Guideline'
                  />
                  <List.Item.Meta
                    avatar={<CheckOutlined />}
                    description='Fit Interview에서 드러내야 하는 Key Value를 질문 유형별로 나누어 구체적이고 세세하게 전달'
                  />
                </List>
              </p>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <ScrollAnimation animateIn='fadeInRight' animateOnce={true} duration={1.5}>
                <Avatar
                  shape='square'
                  src={FinanceProgramAboutSource3}
                  style={{ height: 'auto', width: '100%' }}
                />
              </ScrollAnimation>
            </Col>
          </Row>

          <Row align='middle' gutter={[64, 16]} justify='space-between' style={{ marginBottom: '40px' }}>
            <Col
              xs={{ span: 24, order: 2 }}
              sm={{ span: 24, order: 2 }}
              md={{ span: 12, order: 1 }}
              lg={{ span: 12, order: 1 }}
              xl={{ span: 12, order: 1 }}
            >
              <ScrollAnimation animateIn='fadeInLeft' animateOnce={true} duration={1.5}>
                <Avatar
                  shape='square'
                  src={FinanceProgramAboutSource1}
                  style={{ height: 'auto', width: '100%' }}
                />
              </ScrollAnimation>
            </Col>
            <Col
              xs={{ span: 24, order: 1 }}
              sm={{ span: 24, order: 1 }}
              md={{ span: 12, order: 2 }}
              lg={{ span: 12, order: 2 }}
              xl={{ span: 12, order: 2 }}
            >
              <p className='step-info step-right'>
                <Title level={2}>멘티 맞춤형 1:1 멘토링</Title>
                <List split={false}>
                  <List.Item.Meta
                    avatar={<CheckOutlined />}
                    description='60분간 진행되는 1:1 비대면 멘토링'
                  />
                  <List.Item.Meta
                    avatar={<CheckOutlined />}
                    description='다양한 Global Top-tier Finance Firm의 전/현직자 Pool'
                  />
                </List>
                <Row justify='start' gutter={[16, 16]} style={{ marginTop: '20px' }}>
                  <Col span={10}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconBagSource} />
                      Investment Banking
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconBagSource} />
                      Private Equity
                    </div>
                  </Col>
                  <Col span={10}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconBagSource} />
                      Asset Management
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconBagSource} />
                      Hedge Fund
                    </div>
                  </Col>
                </Row>
              </p>
            </Col>
          </Row>
          <Row align='middle' gutter={[64, 16]} justify='space-between' style={{ marginBottom: '40px' }}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <p className='step-info step-left'>
                <Title level={2}>자유로운 멘토링 주제 선정</Title>
                <List split={false}>
                  <List.Item.Meta
                    avatar={<CheckOutlined />}
                    description='나에게 가장 필요한 토픽을 자유롭게 선택'
                  />
                  <List.Item.Meta
                    avatar={<CheckOutlined />}
                    description='Top-tier 멘토들에게 듣는 현실적인 조언부터 실무 Training까지'
                  />
                </List>
                <Row justify='start' gutter={[16, 16]} style={{ marginTop: '20px' }}>
                  <Col span={10}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconCheckSource} />
                      Resume
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconCheckSource} />
                      Essay Edit
                    </div>
                  </Col>
                  <Col span={10}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconCheckSource} />
                      Interview Preparation
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='name-list'>
                      <img alt='Icon' src={IconCheckSource} />
                      Modeling
                    </div>
                  </Col>
                </Row>
              </p>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <ScrollAnimation animateIn='fadeInRight' animateOnce={true} duration={1.5}>
                <Avatar
                  shape='square'
                  src={FinanceProgramAboutSource2}
                  style={{ height: 'auto', width: '100%' }}
                />
              </ScrollAnimation>
            </Col>
          </Row>
        </div>
      </div>

      <PriceGuideContainer>
        <PriceGuideBox>
          <GuidePriceHeader style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Title>Finance Contents 구매하기</Title>
            <Text>* Finance Frameworks 무제한 이용권은 환불 서비스 제공 X</Text>
          </GuidePriceHeader>

          <GuidePriceTickets>
            <div>
              <PriceGuideTicket>
                <Title>Finance Frameworks 무제한 이용권</Title>

                <Text>
                  <span className='discount'>정가 5만원</span>
                  <span className='price'>3</span>
                  <span className='priceTag'>만원 (40% 할인) </span>
                  <span className='period'>/ 3개월</span>
                </Text>
              </PriceGuideTicket>

              {currentUser?.is_finance_frameworks_membership ? (
                <Link to={`${ERoute.CSLearningBoard}`}>
                  <Button className='secondary purchased'>이미 구매하셨습니다</Button>
                </Link>
              ) : (
                <Link to={`/purchase/finance_frameworks`}>
                  <Button className='secondary'>구매하기</Button>
                </Link>
              )}
            </div>

            <div>
              <PriceGuideTicket>
                <Title>Mock 인터뷰 이용권(60분)</Title>

                <Text>
                  <span className='price'>30</span>
                  <span className='priceTag'>만원</span> <span className='period'>/1회</span>
                </Text>
              </PriceGuideTicket>

              <Link to={'/purchase/finance_mentoring_ticket?mentoring_type=60minutes'}>
                <Button className='secondary'>구매하기</Button>
              </Link>
            </div>
          </GuidePriceTickets>
        </PriceGuideBox>
      </PriceGuideContainer>
    </ConsultingProgramAboutWrapper>
  );
};

export default FinanceProgramAbout;

const PriceGuideContainer = styled.section`
  max-width: 1440px;
  margin: 0 auto;
  padding: 50px 50px 70px 50px;

  @media screen and ${device.lg} {
    padding: 35px 20px;
  }

  button.secondary {
    display: block;
    width: 134px;
    height: 44px;
    margin: 0 auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideBox = styled.div`
  margin-top: 25px;
  padding: 30px 45px;
  border: 1px solid ${SecondaryColor};
  border-radius: 10px;
  background: #ffffff;

  @media screen and ${device.lg} {
    padding: 25px 15px;
  }

  .secondSubTitle {
    display: block;
    margin: 10px 0 0 0;
    color: ${PrimaryColor};
  }
`;

const GuidePriceHeader = styled.header`
  display: flex;
  align-items: center;

  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: flex-start;
  }

  h1.ant-typography {
    margin: 0 10px 0 0;
    color: #141121;
    font-size: 20px;
  }

  span.ant-typography {
    color: #747474;
  }
`;

const GuidePriceTickets = styled.div`
  display: flex;
  justify-content: center;

  @media screen and ${device.lg} {
    flex-direction: column;
  }

  .ant-btn.secondary.purchased {
    width: auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideTicket = styled.div`
  width: 356px;
  margin: 15px 15px;
  padding: 25px 0;
  border-radius: 10px;
  background: #f9f9f9;
  white-space: pre-wrap;

  @media screen and ${device.xl} {
    width: 260px;
  }

  @media screen and ${device.lg} {
    width: 100%;
    margin: 25px 0;
  }

  h1.ant-typography {
    margin: 0;
    font-size: 18px;
    text-align: center;
    line-height: 26px;
    color: ${PrimaryColor};

    @media screen and ${device.xl} {
      font-size: 16px;
    }

    @media screen and ${device.lg} {
      font-size: 18px;
    }
  }

  span.ant-typography {
    display: block;
    text-align: center;

    .price {
      margin-right: 5px;
      font-size: 48px;
      color: ${SecondaryColor};
    }

    .priceTag {
      font-size: 18px;
      color: ${SecondaryColor};
    }

    .period,
    .discount {
      font-size: 18px;
      color: #747474;
    }

    .discount {
      margin-right: 10px;
      text-decoration: line-through;
    }
  }
`;
